/**
 * collection.js
 * XStamper — Collection page interactive behaviours
 *
 * Handles:
 *  1. Filter group accordion (expand / collapse per group)
 *  2. Mobile filter drawer (open / close + overlay)
 *  3. Sort-by select → AJAX section update
 *  4. View toggle (grid / list)
 *  5. Filter checkbox → AJAX section update
 *  6. Price-range apply button → AJAX section update
 *  7. Active-filter pills / clear links / pagination → AJAX section update
 *  8. Quick view modal
 *
 * Filter / sort / pagination changes re-render ONLY this section via the
 * Section Rendering API (?sections=<id>) and swap the HTML in place — no
 * full page reload, no white flash. The URL stays in sync via
 * history.pushState; back/forward re-render on popstate. If the section
 * fetch fails we fall back to a normal navigation.
 *
 * All listeners are delegated from the persistent .shopify-section
 * wrapper (or document), so they survive innerHTML swaps without
 * rebinding and never stack duplicates.
 *
 * Wishlist is owned by product-interactions.js (global) via delegated
 * [data-wishlist] handling and the 'xstamper_wishlist' localStorage key.
 * After each swap this file dispatches 'wishlist:sync' so heart states
 * refresh on the fresh product cards.
 *
 * Vanilla JS, no dependencies.
 * Supports multiple collection sections on the same page.
 */

(function () {
  'use strict';

  /* ── Registry of live collection sections ─────────────────────────────── */

  var registry = []; // [{ el, id, token, controller }]

  /* ── Helpers ────────────────────────────────────────────────────────────── */

  /**
   * Build a URL string from the current location, setting / removing params.
   * @param {Object} set    { paramName: value } — set these params
   * @param {string[]} remove — remove these param names
   * @returns {string}
   */
  function buildUrl(set, remove) {
    var url = new URL(window.location.href);
    if (set) {
      Object.keys(set).forEach(function (key) {
        url.searchParams.set(key, set[key]);
      });
    }
    if (remove) {
      remove.forEach(function (key) {
        url.searchParams.delete(key);
      });
    }
    return url.toString();
  }

  /** Strip the Section Rendering API param so it never lands in the address bar. */
  function displayUrlFor(url) {
    var u = new URL(url, window.location.origin);
    u.searchParams.delete('sections');
    return u.toString();
  }

  /** Sticky header height + breathing room, for viewport anchoring. */
  function headerOffset() {
    var header = document.getElementById('site-header');
    return (header ? header.offsetHeight : 72) + 16;
  }

  /* ═════════════════════════════════════════════════════════════════════════
     AJAX SECTION RENDERING
     Fetch this section's HTML for the target URL via the Section Rendering
     API and swap it in place — the fast path for every filter / sort /
     pagination change. Falls back to a plain navigation on any failure.
  ═════════════════════════════════════════════════════════════════════════ */

  function renderSection(entry, url, opts) {
    opts = opts || {};

    // No section wrapper (unexpected layout) — plain navigation still works
    if (!entry.id) {
      window.location.assign(displayUrlFor(url));
      return;
    }

    var sectionEl = entry.el;
    var target = new URL(url, window.location.origin);
    target.searchParams.set('sections', entry.id);

    // Supersede any in-flight update for this section
    entry.token += 1;
    var token = entry.token;
    if (entry.controller) entry.controller.abort();
    var controller = ('AbortController' in window) ? new AbortController() : null;
    entry.controller = controller;

    // Loading state — dim the section instead of hiding the page
    sectionEl.classList.add('is-updating');

    // Snapshot drawer + sidebar scroll state so filtering doesn't bounce
    // the drawer closed or lose the user's place in a long filter list
    var sidebar = sectionEl.querySelector('.filter-sidebar');
    var drawerWasOpen = !!(sidebar && sidebar.classList.contains('is-open'));
    var sidebarScrollTop = sidebar ? sidebar.scrollTop : 0;

    // Safety net: if the request hangs, give up on AJAX and navigate
    var timeoutId = setTimeout(function () {
      if (entry.token === token) window.location.assign(displayUrlFor(url));
    }, 12000);

    fetch(target.toString(), controller ? { signal: controller.signal } : undefined)
      .then(function (r) {
        if (!r.ok) throw new Error('Section render failed: ' + r.status);
        return r.json();
      })
      .then(function (data) {
        if (entry.token !== token) return; // superseded by a newer update

        var html = data && data[entry.id];
        if (!html) throw new Error('Section render returned no HTML');

        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.getElementById(sectionEl.id) || doc.body.firstElementChild;
        if (!fresh) throw new Error('Section render markup missing');

        // <script> tags injected via innerHTML do not execute, so the
        // section's own script include cannot double-init.
        sectionEl.innerHTML = fresh.innerHTML;

        if (opts.push !== false) {
          history.pushState({ clAjax: true }, '', displayUrlFor(url));
        }

        afterRender(entry, { drawerWasOpen: drawerWasOpen, sidebarScrollTop: sidebarScrollTop });

        // Anchor the viewport at the grid start (filters + products), never
        // the page top — also prevents the browser's clamp-jump when shorter
        // results collapse the page height. Skipped while the mobile drawer
        // is open (its page scroll is locked) and on popstate (the browser
        // restores scroll itself).
        if (opts.scroll !== false && !drawerWasOpen) {
          // Anchor where the change happened. A category swap rewrites the
          // rail as well as the grid, so anchoring at the grid would scroll
          // the cards the customer just clicked out of view; a filter change
          // leaves the rail alone, so the grid is the right anchor there.
          var anchor = (opts.anchor === 'rail' && sectionEl.querySelector('.cat-rail')) ||
                       sectionEl.querySelector('.collection-layout') ||
                       sectionEl;
          var top = anchor.getBoundingClientRect().top + window.pageYOffset - headerOffset();
          window.scrollTo(0, Math.max(top, 0));
        }
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return; // superseded — newer update owns the UI
        if (entry.token !== token) return;
        // Fallback: plain navigation (pre-AJAX behaviour)
        window.location.assign(displayUrlFor(url));
      })
      .then(function () {
        clearTimeout(timeoutId);
        if (entry.token === token) {
          entry.controller = null;
          sectionEl.classList.remove('is-updating');
        }
      });
  }

  /** Restore client-side state on the freshly swapped-in section HTML. */
  function afterRender(entry, state) {
    var sectionEl = entry.el;
    var sidebar = sectionEl.querySelector('.filter-sidebar');

    if (state.drawerWasOpen) {
      // Re-open without moving focus so the user keeps tapping filters
      var overlay = sectionEl.querySelector('.filter-overlay');
      var openBtn = sectionEl.querySelector('[data-filter-toggle]');
      if (sidebar) {
        sidebar.classList.add('is-open');
        sidebar.setAttribute('role', 'dialog');
        sidebar.setAttribute('aria-modal', 'true');
      }
      if (overlay) overlay.classList.add('is-open');
      if (openBtn) openBtn.setAttribute('aria-expanded', 'true');
    } else {
      document.body.style.overflow = '';
    }

    // Keep the user's place in a long filter list (the sidebar has its own
    // overflow-y scroll that an innerHTML swap would otherwise reset)
    if (sidebar && state.sidebarScrollTop) sidebar.scrollTop = state.sidebarScrollTop;

    applySavedView(sectionEl);
    initStaticTypeCounts(sectionEl);
    applyTreeState(sectionEl);
    initCatRails(sectionEl);

    // Heart states on the fresh product cards (handled by product-interactions.js)
    document.dispatchEvent(new CustomEvent('wishlist:sync'));
  }

  /* ═════════════════════════════════════════════════════════════════════════
     CATEGORY TREE ACCORDION
     Each [data-tree-item] row in the scoped Category group has a ▾ toggle
     that shows / hides its subcategories without touching the filter.
     Liquid decides the default (open when the row or a child is active);
     manual toggles are remembered per row in sessionStorage so a swap
     doesn't snap a row the user opened back shut.
  ═════════════════════════════════════════════════════════════════════════ */

  var TREE_STATE_KEY = 'cl-tree-open';

  function readTreeState() {
    try { return JSON.parse(sessionStorage.getItem(TREE_STATE_KEY) || '{}') || {}; }
    catch (_) { return {}; }
  }

  function setTreeItemOpen(item, open) {
    item.classList.toggle('is-open', open);
    var btn = item.querySelector('[data-tree-toggle]');
    if (btn) {
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      var label = btn.getAttribute('aria-label') || '';
      btn.setAttribute('aria-label', label.replace(/^(Show|Hide)/, open ? 'Hide' : 'Show'));
    }
  }

  function toggleTreeItem(toggle) {
    var item = toggle.closest('[data-tree-item]');
    if (!item) return;
    var open = !item.classList.contains('is-open');
    setTreeItemOpen(item, open);
    var key = item.getAttribute('data-tree-key');
    if (!key) return;
    var state = readTreeState();
    state[key] = open;
    try { sessionStorage.setItem(TREE_STATE_KEY, JSON.stringify(state)); } catch (_) {}
  }

  /* ═════════════════════════════════════════════════════════════════════════
     CATEGORY RAIL ARROWS
     The category cards scroll horizontally. The two arrows page the rail by
     roughly one screenful and hide themselves at each end, so a row that
     already fits shows no arrows at all. Purely presentational — the cards
     themselves keep their normal link behaviour.
  ═════════════════════════════════════════════════════════════════════════ */

  /** Show / hide the arrows for one rail based on where it is scrolled. */
  function syncRailArrows(rail) {
    var track = rail.querySelector('[data-cat-track]');
    var prev  = rail.querySelector('[data-cat-prev]');
    var next  = rail.querySelector('[data-cat-next]');
    if (!track) return;

    // 1px slack absorbs sub-pixel widths from zoom / fractional scaling
    var max = track.scrollWidth - track.clientWidth;
    var overflows = max > 1;
    if (prev) prev.hidden = !overflows || track.scrollLeft <= 1;
    if (next) next.hidden = !overflows || track.scrollLeft >= max - 1;
  }

  /** Page the rail one screenful (minus a card's peek) in either direction. */
  function scrollRail(rail, dir) {
    var track = rail.querySelector('[data-cat-track]');
    if (!track) return;
    var card = track.querySelector('.cat-circle');
    var step = card ? card.offsetWidth + 16 : 200;
    // Whole cards per page, never less than one
    var perPage = Math.max(1, Math.floor(track.clientWidth / step));
    track.scrollBy({ left: dir * step * perPage, behavior: 'smooth' });
  }

  /** Wire every rail in a freshly rendered section. */
  function initCatRails(root) {
    root.querySelectorAll('[data-cat-rail]').forEach(function (rail) {
      var track = rail.querySelector('[data-cat-track]');
      if (!track || track.dataset.railBound === '1') return;
      track.dataset.railBound = '1';

      var pending = false;
      track.addEventListener('scroll', function () {
        // Coalesce the scroll storm into one measurement per frame
        if (pending) return;
        pending = true;
        requestAnimationFrame(function () {
          pending = false;
          syncRailArrows(rail);
        });
      }, { passive: true });

      if ('ResizeObserver' in window) {
        new ResizeObserver(function () { syncRailArrows(rail); }).observe(track);
      }
      syncRailArrows(rail);
    });
  }

  /** Re-apply remembered open / closed rows on a freshly rendered tree. */
  function applyTreeState(root) {
    var state = readTreeState();
    root.querySelectorAll('[data-tree-item][data-tree-key]').forEach(function (item) {
      var key = item.getAttribute('data-tree-key');
      if (Object.prototype.hasOwnProperty.call(state, key)) setTreeItemOpen(item, !!state[key]);
    });
  }

  /* ═════════════════════════════════════════════════════════════════════════
     2. MOBILE FILTER DRAWER
     Elements are resolved at call time — they are replaced on every swap.
  ═════════════════════════════════════════════════════════════════════════ */

  function openDrawer(sectionEl) {
    var sidebar = sectionEl.querySelector('.filter-sidebar');
    var overlay = sectionEl.querySelector('.filter-overlay');
    var openBtn = sectionEl.querySelector('[data-filter-toggle]');
    if (!sidebar) return;

    sidebar.classList.add('is-open');
    // Only a modal dialog while it is the mobile slide-in drawer
    sidebar.setAttribute('role', 'dialog');
    sidebar.setAttribute('aria-modal', 'true');
    if (overlay) overlay.classList.add('is-open');
    if (openBtn) openBtn.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';   // prevent body scroll while drawer is open

    var firstFocusable = sidebar.querySelector('a, button, input, select, [tabindex]:not([tabindex="-1"])');
    if (firstFocusable) firstFocusable.focus();
  }

  function closeDrawer(sectionEl) {
    var sidebar = sectionEl.querySelector('.filter-sidebar');
    var overlay = sectionEl.querySelector('.filter-overlay');
    var openBtn = sectionEl.querySelector('[data-filter-toggle]');
    if (!sidebar) return;

    sidebar.classList.remove('is-open');
    sidebar.removeAttribute('role');
    sidebar.removeAttribute('aria-modal');
    if (overlay) overlay.classList.remove('is-open');
    if (openBtn) {
      openBtn.setAttribute('aria-expanded', 'false');
      openBtn.focus();   // return focus to the trigger
    }
    document.body.style.overflow = '';
  }

  /* ═════════════════════════════════════════════════════════════════════════
     4. VIEW TOGGLE (grid / list)
  ═════════════════════════════════════════════════════════════════════════ */

  function setView(root, view) {
    var grid = root.querySelector('.product-grid[data-view]');
    if (grid) grid.setAttribute('data-view', view);

    root.querySelectorAll('.view-toggle__btn[data-view]').forEach(function (b) {
      var isActive = b.getAttribute('data-view') === view;
      b.classList.toggle('is-active', isActive);
      b.setAttribute('aria-pressed', isActive.toString());
    });
  }

  function applySavedView(root) {
    var view = null;
    try { view = sessionStorage.getItem('cl-view'); } catch (_) {}
    if (view) setView(root, view);
  }

  /* ═════════════════════════════════════════════════════════════════════════
     5b. CATEGORY FILTERS BY PRODUCT TYPE
     The sidebar swaps the standard Category group for a type-specific
     category metafield filter while a mapped product type (Theme settings →
     Category filters by product type) is selected. Filtering itself is
     native Shopify — this hook only prevents a hidden group's filter from
     lingering invisibly when a mapped type toggles.

     data-mc-map format: "Type::filter.param||Type::filter.param||…";
     the param half is empty until that type's filter exists in Search &
     Discovery (no swap happens then, so nothing needs stripping).
  ═════════════════════════════════════════════════════════════════════════ */

  /**
   * Rewrite a product-type filter URL when a mapped type is involved.
   * - Checking a mapped type: the standard Category group is about to
   *   hide — drop its params.
   * - Unchecking it: that type's own category group is about to hide —
   *   drop its params.
   */
  function mcAdjustTypeUrl(sidebar, cb, urlStr) {
    var entry = null;
    (sidebar.getAttribute('data-mc-map') || '')
      .split('||')
      .forEach(function (raw) {
        var pair = raw.split('::');
        if (pair[0] && pair[0] === cb.getAttribute('data-pt-value')) {
          entry = { param: pair[1] || '' };
        }
      });
    if (!entry || !entry.param) return urlStr;

    var param = cb.checked ? sidebar.getAttribute('data-mc-std-param') : entry.param;
    if (!param) return urlStr;

    var url = new URL(urlStr, window.location.origin);
    url.searchParams.delete(param);
    return url.toString();
  }

  /* ═════════════════════════════════════════════════════════════════════════
     5c. STATIC PRODUCT TYPE COUNTS
     Shopify cross-filters every group's counts by the other active filters,
     so selecting a category shrinks the Product type numbers (and zeroes
     the other types). Per store requirement the type counts must stay at
     their category-independent totals: when any category filter is active,
     re-fetch this section via the Section Rendering API with the category
     params removed and patch the Product type counts/disabled states from
     that render. Purely cosmetic — filtering behavior is untouched.
  ═════════════════════════════════════════════════════════════════════════ */

  function initStaticTypeCounts(root) {
    var sidebar = root.querySelector('.filter-sidebar[data-mc-map]');
    if (!sidebar) return;

    // Category params = every mapped metafield filter + the standard group
    var catParams = (sidebar.getAttribute('data-mc-map') || '')
      .split('||')
      .map(function (e) { return e.split('::')[1]; })
      .filter(Boolean);
    var stdParam = sidebar.getAttribute('data-mc-std-param');
    if (stdParam) catParams.push(stdParam);

    var url = new URL(window.location.href);
    var hasCat = catParams.some(function (p) { return url.searchParams.has(p); });
    if (!hasCat) return; // no category active — counts are already correct

    var sectionEl = sidebar.closest('.shopify-section');
    if (!sectionEl || !sectionEl.id) return;
    var sectionId = sectionEl.id.replace(/^shopify-section-/, '');

    catParams.forEach(function (p) { url.searchParams.delete(p); });
    url.searchParams.delete('page');
    url.searchParams.set('sections', sectionId);

    fetch(url.toString())
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data && data[sectionId];
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');

        var ref = {};
        doc.querySelectorAll('.filter-checkbox__input[data-pt-value]').forEach(function (inp) {
          var row = inp.closest('.filter-checkbox');
          var cnt = row && row.querySelector('.filter-checkbox__count');
          ref[inp.getAttribute('data-pt-value')] = {
            count: cnt ? cnt.textContent : '',
            disabled: inp.disabled
          };
        });

        root.querySelectorAll('.filter-checkbox__input[data-pt-value]').forEach(function (inp) {
          var r = ref[inp.getAttribute('data-pt-value')];
          if (!r) return;
          var row = inp.closest('.filter-checkbox');
          var cnt = row && row.querySelector('.filter-checkbox__count');
          if (cnt && r.count) cnt.textContent = r.count;
          if (!r.disabled && inp.disabled) {
            inp.disabled = false;
            if (row) row.classList.remove('filter-checkbox--disabled');
          }
        });
      })
      .catch(function () { /* cosmetic only — keep native counts on failure */ });
  }

  /* ═════════════════════════════════════════════════════════════════════════
     6. PRICE RANGE APPLY
  ═════════════════════════════════════════════════════════════════════════ */

  function applyPriceRange(entry, applyBtn) {
    var container = applyBtn.closest('[data-price-range]');
    if (!container) return;

    var minInput = container.querySelector('[data-price-min]');
    var maxInput = container.querySelector('[data-price-max]');
    var paramMin = container.getAttribute('data-param-min');
    var paramMax = container.getAttribute('data-param-max');

    var url = new URL(window.location.href);

    // Shopify price filter params expect dollar values (not cents)
    // Inputs already display dollars (filter.min_value.value / 100)
    if (minInput && minInput.value !== '') {
      url.searchParams.set(paramMin, minInput.value);
    } else {
      url.searchParams.delete(paramMin);
    }

    if (maxInput && maxInput.value !== '') {
      url.searchParams.set(paramMax, maxInput.value);
    } else {
      url.searchParams.delete(paramMax);
    }

    // Reset to page 1 when filter changes
    url.searchParams.delete('page');

    renderSection(entry, url.toString());
  }

  /* ═════════════════════════════════════════════════════════════════════════
     7. AJAX-ABLE LINKS
     Active-filter pills, clear links, empty-state clear, pagination —
     all point at filtered collection URLs, so they can swap in place.
  ═════════════════════════════════════════════════════════════════════════ */

  /**
   * The collection a URL belongs to, ignoring any tag segments.
   * /collections/stamps/shachihata+cat-business-stamps -> /collections/stamps
   * Category links append tags to the path, so comparing full pathnames
   * would wrongly treat them as leaving the collection.
   */
  function collectionBase(pathname) {
    var m = pathname.match(/^(\/collections\/[^/]+)/);
    return m ? m[1] : pathname;
  }

  /** Same collection, regardless of which tags are applied? */
  function sameCollection(link) {
    return collectionBase(link.pathname) === collectionBase(window.location.pathname);
  }

  function isAjaxLink(link) {
    if (link.target && link.target !== '_self') return false;

    // Category links change the PATH (they add a cat: tag), not the query.
    // The Section Rendering API renders those fine, so they swap in place
    // like any filter — as long as we stay inside the same collection.
    // A link to a genuinely different collection still navigates normally.
    if (link.closest('.cat-circles') ||
        link.classList.contains('filter-tree__cat') ||
        link.classList.contains('filter-tree__clear')) {
      return sameCollection(link);
    }

    return !!(
      link.closest('.pagination') ||
      link.classList.contains('active-filters__pill') ||
      link.classList.contains('active-filters__clear') ||
      link.classList.contains('filter-sidebar__clear') ||
      link.closest('.collection-empty')
    );
  }

  /* ═════════════════════════════════════════════════════════════════════════
     8. QUICK VIEW MODAL
     Document-delegated once; the modal is resolved at event time because
     every swap re-creates it.
  ═════════════════════════════════════════════════════════════════════════ */

  var qvLastFocus = null;

  function formatMoneyCents(cents) {
    if (window.themeFormatMoney) return window.themeFormatMoney(cents);
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function openQuickView(modal, handle) {
    var imgEl    = modal.querySelector('.qv-modal__image');
    var catEl    = modal.querySelector('.qv-modal__category');
    var titleEl  = modal.querySelector('.qv-modal__title');
    var priceEl  = modal.querySelector('.qv-modal__price');
    var linkEl   = modal.querySelector('.qv-modal__view-btn');
    var closeBtn = modal.querySelector('.qv-modal__close');

    // Reset to loading state — keep the image invisible until it has
    // actually loaded so the broken-image icon never shows
    if (catEl)   catEl.textContent = '';
    if (titleEl) titleEl.textContent = 'Loading…';
    if (priceEl) priceEl.textContent = '';
    if (imgEl) {
      imgEl.style.opacity = '0';
      imgEl.removeAttribute('src');
      imgEl.alt = '';
      imgEl.onload = function () { imgEl.style.opacity = '1'; };
    }
    if (linkEl)  linkEl.href = '/products/' + handle;

    qvLastFocus = document.activeElement;
    modal.setAttribute('aria-hidden', 'false');
    modal.removeAttribute('inert');
    modal.classList.add('is-open');
    document.documentElement.style.overflow = 'hidden';
    if (closeBtn) closeBtn.focus();

    fetch('/products/' + handle + '.js')
      .then(function (r) {
        if (!r.ok) throw new Error('Quick view request failed: ' + r.status);
        return r.json();
      })
      .then(function (p) {
        if (imgEl && p.featured_image) {
          // Shopify CDN image resize
          imgEl.src = p.featured_image.replace(/\.jpg|\.jpeg|\.png|\.webp/i, function (ext) {
            return '_400x400' + ext;
          });
        }
        if (imgEl)   imgEl.alt = p.title;
        if (catEl)   catEl.textContent = p.product_type || '';
        if (titleEl) titleEl.textContent = p.title;
        if (priceEl) priceEl.textContent = formatMoneyCents(p.price);
        if (linkEl)  linkEl.href = '/products/' + p.handle;
      })
      .catch(function () {
        if (titleEl) titleEl.textContent = 'Could not load product details.';
      });
  }

  function closeQuickView(modal) {
    modal.setAttribute('aria-hidden', 'true');
    modal.setAttribute('inert', '');
    modal.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    if (qvLastFocus) qvLastFocus.focus();
  }

  /* ═════════════════════════════════════════════════════════════════════════
     DELEGATED SECTION HANDLERS — bound ONCE per section wrapper
  ═════════════════════════════════════════════════════════════════════════ */

  function bindSectionHandlers(entry) {
    var sectionEl = entry.el;

    sectionEl.addEventListener('click', function (e) {
      // 1. Filter group accordion
      var toggle = e.target.closest('[data-filter-toggle-group]');
      if (toggle) {
        var group = toggle.closest('[data-filter-group]');
        if (group) {
          var isOpen = group.classList.contains('is-open');
          group.classList.toggle('is-open', !isOpen);
          // Accessibility: update aria-expanded
          toggle.setAttribute('aria-expanded', (!isOpen).toString());
        }
        return;
      }

      // 1b. Category tree ▾ — expand / collapse subcategories, never filters
      var treeToggle = e.target.closest('[data-tree-toggle]');
      if (treeToggle) { toggleTreeItem(treeToggle); return; }

      // 1c. Category rail arrows — page the cards, never filter
      var railBtn = e.target.closest('[data-cat-prev], [data-cat-next]');
      if (railBtn) {
        var rail = railBtn.closest('[data-cat-rail]');
        if (rail) scrollRail(rail, railBtn.hasAttribute('data-cat-next') ? 1 : -1);
        return;
      }

      // 2. Mobile drawer open / close
      if (e.target.closest('[data-filter-toggle]')) { openDrawer(sectionEl); return; }
      if (e.target.closest('[data-filter-close]')) { closeDrawer(sectionEl); return; }
      if (e.target.closest('.filter-overlay'))     { closeDrawer(sectionEl); return; }

      // 4. View toggle
      var viewBtn = e.target.closest('.view-toggle__btn[data-view]');
      if (viewBtn) {
        var view = viewBtn.getAttribute('data-view');
        setView(sectionEl, view);
        // Persist preference so it survives swaps and pagination
        try { sessionStorage.setItem('cl-view', view); } catch (_) {}
        return;
      }

      // 6. Price range apply
      var applyBtn = e.target.closest('[data-price-apply]');
      if (applyBtn) { applyPriceRange(entry, applyBtn); return; }

      // 7. Pills / clear links / pagination
      var link = e.target.closest('a');
      if (link && isAjaxLink(link)) {
        e.preventDefault();
        // A category click rebuilds the rail — keep it in view afterwards
        var isCategory = !!(link.closest('.cat-circles') ||
                            link.classList.contains('filter-tree__cat') ||
                            link.classList.contains('filter-tree__clear'));
        renderSection(entry, link.href, isCategory ? { anchor: 'rail' } : undefined);
      }
    });

    sectionEl.addEventListener('change', function (e) {
      // 3. Sort select
      if (e.target.classList.contains('sort-select')) {
        renderSection(entry, buildUrl({ 'sort_by': e.target.value }, null));
        return;
      }

      // 5. Filter checkboxes
      var cb = e.target.closest('.filter-checkbox__input[data-url-add]');
      if (cb) {
        var url = cb.checked
          ? cb.getAttribute('data-url-add')
          : cb.getAttribute('data-url-remove');

        // Product type is single-select (rendered as a radio). Shopify's
        // url_to_add appends another filter.p.product_type, which is what let
        // two types stack up while the category list below could only show
        // one. Rebuild the URL with exactly the picked type instead, leaving
        // every other filter, the tag path and the sort untouched.
        if (cb.type === 'radio' && cb.hasAttribute('data-pt-value')) {
          try {
            var ptUrl = new URL(cb.getAttribute('data-url-add'), window.location.origin);
            ptUrl.searchParams.delete('filter.p.product_type');
            ptUrl.searchParams.set('filter.p.product_type', cb.getAttribute('data-pt-value'));
            url = ptUrl.pathname + ptUrl.search;
          } catch (_) { /* keep Shopify's URL if URL() is unavailable */ }
        }

        var mcSidebar = sectionEl.querySelector('.filter-sidebar[data-mc-map]');
        if (url && mcSidebar) url = mcAdjustTypeUrl(mcSidebar, cb, url);
        if (url) renderSection(entry, url);
      }
    });

    // Allow pressing Enter in either price input to trigger apply
    sectionEl.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') return;
      var input = e.target.closest('[data-price-min], [data-price-max]');
      if (!input) return;
      e.preventDefault();
      var container = input.closest('[data-price-range]');
      var applyBtn = container && container.querySelector('[data-price-apply]');
      if (applyBtn) applyBtn.click();
    });
  }

  /* ═════════════════════════════════════════════════════════════════════════
     DOCUMENT-LEVEL HANDLERS — bound once for the whole page
  ═════════════════════════════════════════════════════════════════════════ */

  function bindGlobalHandlers() {
    // Quick view open / close
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-quick-view]');
      if (btn) {
        var section = btn.closest('.shopify-section');
        var modal = (section && section.querySelector('[data-qv-modal]')) ||
                    document.querySelector('[data-qv-modal]');
        if (modal) openQuickView(modal, btn.getAttribute('data-quick-view'));
        return;
      }

      var closer = e.target.closest('.qv-modal__backdrop, .qv-modal__close');
      if (closer) {
        var m = closer.closest('[data-qv-modal]');
        if (m) closeQuickView(m);
      }
    });

    // Escape closes quick view first, then any open filter drawer
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' && e.key !== 'Esc') return;

      var openModal = document.querySelector('[data-qv-modal].is-open');
      if (openModal) { closeQuickView(openModal); return; }

      registry.forEach(function (entry) {
        if (entry.el.querySelector('.filter-sidebar.is-open')) closeDrawer(entry.el);
      });
    });

    // Back / forward — re-render sections for the restored URL
    // (scroll: false — the browser restores scroll position itself)
    window.addEventListener('popstate', function () {
      registry.forEach(function (entry) {
        renderSection(entry, window.location.href, { push: false, scroll: false });
      });
    });
  }

  /* ═════════════════════════════════════════════════════════════════════════
     INIT — run once for each collection section on the page
  ═════════════════════════════════════════════════════════════════════════ */

  function init() {
    document.querySelectorAll('.cl-wrap').forEach(function (wrap) {
      var sectionEl = wrap.closest('.shopify-section');

      // Fallback: no section wrapper — bind to document, AJAX disabled
      // (renderSection falls back to plain navigation when id is null)
      var el = sectionEl || document.documentElement;
      if (el.__clInit) return;   // guard against double init
      el.__clInit = true;

      var entry = {
        el: el,
        id: (sectionEl && sectionEl.id) ? sectionEl.id.replace(/^shopify-section-/, '') : null,
        token: 0,
        controller: null
      };
      registry.push(entry);
      bindSectionHandlers(entry);
      applySavedView(el);
      initStaticTypeCounts(el);
      applyTreeState(el);
      initCatRails(el);
    });

    if (registry.length) bindGlobalHandlers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
